import React from "react"
import { ListGroup, ListGroupItem, Container } from "reactstrap"
import { Trans, useLingui } from "@lingui/react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import uuid from "uuid"
import { itemsFetchBalances } from "../../store/actions/getVslaBalance"
import { selectBalance } from "../../store/reducers/getVslaBalance"
import { selectVslaDebtBalance } from "../../store/reducers/getVslaBalance"
import { getCurrency } from "../../view/shared/utilities/currency"
import { getBalances } from "../../store/reducers/transactions/actions"

const headerStyle = {
  color: "#E36F1E",
  fontFamily: "quicksand",
  fontWeight: "bolder",
  fontSize: "1.8em",
  textAlign: "center",
  paddingBottom: "0.5em",
  margin: "34px 0 0 0",
}

const loadingStyle = {
  fontFamily: "Fira Sans",
  fontSize: "20px",
}

const summary = {
  ar: {
    savings: "المدخرات",
    loan_interest_due: "فائدة القرض",
    Debt: "الدين",
    membership: "العضوية",
    fine: "الغرامة",
    welfare: "الرعاية",
    shares: ":عدد الأسهم",
    loan: "قروض غير مدفوعة",
    "fine paid": "Fines fund",
    welfare_requests: "إجمالي طلبات الرعاية",
    agri: "الصندوق الزراعي",
  },
  en: {
    agri: "Agricultural fund",
    savings: "Savings fund",
    SAVINGS: "Savings fund",
    shares: "Number of shares",
    loan_interest_due: "Loan interest fund",
    loan: "Unpaid loans",
    Debt: "Debt",
    membership: "Membership fee fund",
    welfare_requests: "Total welfare requests",
    "fine paid": "Fines fund",
    welfare: "Welfare fund",
  },
  fr: {
    agri: "Fonds agricole",
    savings: "Économies",
    loan_interest_due: "Intérêts d'emprunt",
    Debt: "Dette",
    membership: "Adhésion",
    "fine paid": "Amende",
    welfare: "Sociale",
  },
  sw: {
    agri: "Funda ya Ushughuli wa Kukulipa",
    savings: "Mfuku wa Akiba",
    shares: "Hizi zilizopo",
    loan: "Mikopo haijalipwa",
    loan_interest_due: "Mfuku wa riba ya mkopo",
    Debt: "Deni",
    membership: "Mfuko wa ada ya uanachama",
    "fine paid": "Mfuko wa faini",
    welfare: "Mfuko wa jamii",
  },
  es: {
    agri: "Fondo agrícola",
    savings: "Ahorro",
    loan_interest_due: "Interés del préstamo",
    Debt: "Deuda",
    membership: "Membresía",
    fine: "Multa",
    "fine paid": "Fondo de Multas",
    welfare: "Bienestar",
  },
  lb: {
    agri: "agricultural fund",
    savings: "Savings fund",
    shares: "Number of shares",
    loan_interest_due: "Loan interest fund",
    loan: "Unpaid loans",
    Debt: "Debt",
    membership: "Membership fee fund",
    welfare_requests: "Total welfare requests",
    "fine paid": "Fines fund",
    welfare: "Welfare fund",
    fine: "Fines fund",
  },
  pt: {
    agri: "Fundo agrícola",
    savings: "Poupanças",
    loan_interest_due: "Juros do empréstimo",
    Debt: "Dívida",
    membership: "Filiação de Mebro",
    fine: "Multa",
    welfare: "Fundo Social",
    shares: "Número de Ações",
    loan: "Empréstimos não pagos",
    welfare_requests: "Pedidos Totais de Fundo Social",
    "fine paid": "Fundo de Multas",
  },
}

export const processBalanceData = (balance, welfareReqs) => {
  const sumWelfareRequests = (welfareReqs || []).reduce((acc, item) => {
    acc += item.balance
    return acc
  }, 0)
  const item = {
    ...welfareReqs[0],
    balance: sumWelfareRequests,
  }
  return [...balance, item]
    .filter(
      (balance) =>
        balance.balance > 0 &&
        [
          "savings",
          "fine paid",
          "loan",
          "shares",
          "loan_interest_due",
          "membership",
          "welfare",
          "fine",
          "agri"
        ].includes(balance.accountType),
    )
    .sort((a, b) => (a.balance > b.balance ? 0 : b.balance > a.balance ? 1 : -1))
    .map((balance) => {
      return [
        {
          issuer: balance.issuer,
          asset: balance.assetType,
          type: balance.accountType,
          amount: balance.balance,
        },
      ]
    })
}

const FundSummary = (props) => {
  const { i18n } = useLingui()
  const [processedData, setProcessedData] = React.useState([])
  const setHook = (fn, args) => fn(args)
  React.useEffect(() => {
    const { account, fetchBalances } = props
    const vslaBalance = processBalanceData(
      Object.values(account.balances),
      Object.values(account.members).filter(
        ({ welfare_requests }) => welfare_requests,
      ),
    )
    setHook(
      setProcessedData,
      ["ar", "lb"].includes(i18n.locale)
        ? vslaBalance.filter((bal) => bal[0].type !== "shares")
        : vslaBalance,
    )
    fetchBalances()
  }, [])

  React.useEffect(() => {
    const { account } = props
    const vslaBalance = processBalanceData(
      Object.values(account.balances),
      Object.values(account.members).filter(
        ({ welfare_requests }) => welfare_requests,
      ),
    )
    setHook(
      setProcessedData,
      ["ar", "lb"].includes(i18n.locale)
        ? vslaBalance.filter((bal) => bal[0].type !== "shares")
        : vslaBalance,
    )
  }, [props.account])

  const isProcessing = () => {
    return (
      (props.isloading && !props.vslaBalance.length) ||
      Object.keys(props.pendingTransactions)
        .filter((key) => key.split("_").length === 3)
        .map((key) => props.pendingTransactions[key]).length > 0
    )
  }

  if (!isProcessing() && !props.vslaBalance.length) {
    return (
      <div>
        <h4 style={headerStyle}>
          <Trans id="menu.funds" />
        </h4>
        <center>
          <span>
            <Trans id="span.no_records_found" />
          </span>
        </center>
      </div>
    )
  }

  return (
    <div>
      <div>
        <h4 style={headerStyle}>
          <Trans id="menu.funds" />
        </h4>
      </div>
      {isProcessing() && (
        <span
          className="dots-loader"
          style={{
            color: "#E36F1E",
            display: "flex",
            justifyContent: "center",
            fontSize: 12,
            margin: "auto",
          }}
        >
          <div style={{ display: "flex", width: 70 }}>
            <div>
              <Trans id="span.processing" />
            </div>
          </div>
        </span>
      )}
      <Container>
        <ListGroup
          style={{
            textAlign: i18n.locale === "ar" ? "right" : "initial",
          }}
          flush
        >
          {processedData.map((bal) => (
            <ListGroupItem
              key={uuid.v4()}
              className="textStyle"
              style={loadingStyle}
            >
              {summary[i18n.locale][bal[0].type]}:{" "}
              {bal[0].type === "shares"
                ? parseFloat(
                  bal[0].amount / parseFloat(props.group?.cycle?.shareValue),
                ).toFixed(2)
                : parseFloat(bal[0].amount).toFixed(2)}{" "}
              {bal[0].type !== "shares" &&
                !["ar", "lb"].includes(i18n.locale) &&
                getCurrency(props.group?.country).symbol}
              {bal[0].type !== "shares" &&
                ["ar", "lb"].includes(i18n.locale) &&
                (props.vsla?.metadata?.currency === "USD"
                  ? getCurrency(props.group?.country).usd_symbol
                  : getCurrency(props.group?.country).lbp_symbol)}
            </ListGroupItem>
          ))}
        </ListGroup>
      </Container>
    </div>
  )
}

FundSummary.propTypes = {
  account: PropTypes.object,
  fetchBalances: PropTypes.func,
  isloading: PropTypes.bool,
  vslaBalance: PropTypes.array,
  pendingTransactions: PropTypes.object,
  vsla: PropTypes.object,
  group: PropTypes.object,
}

const mapStateToProps = (state) => {
  return {
    error: state.balanceError,
    isloading: state.vslaBalanceLoading,
    vslaBalance: selectBalance(state),
    vslaDebt: selectVslaDebtBalance(state),
    pendingTransactions: state.pendingTransactions,
    group: state.group,
    account: state.account,
    vsla: state.vsla,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    fetchData: (url, headers) => dispatch(itemsFetchBalances(url, headers)),
    fetchBalances: () => dispatch(getBalances()),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(FundSummary)
